import { LOBBY_TTL_MS } from '@sh/shared'
import type { Seat } from '@sh/shared'

const key = (code: string) => `sh:seat:${code.toUpperCase()}`
const NAME_KEY = 'sh:name'
const RECENT_KEY = 'sh:recent'

/**
 * The seat token lives in localStorage so a refresh, a screen lock or a browser
 * eviction does not cost you your role. It is scoped per lobby code.
 */
export function loadSeat(code: string): Seat | null {
  try {
    const raw = localStorage.getItem(key(code))
    if (!raw) return null
    const seat = JSON.parse(raw) as Seat
    return seat.playerId && seat.token ? seat : null
  } catch {
    return null
  }
}

export function saveSeat(seat: Seat): void {
  try {
    localStorage.setItem(key(seat.code), JSON.stringify(seat))
    localStorage.setItem(
      RECENT_KEY,
      JSON.stringify({ code: seat.code.toUpperCase(), at: Date.now() }),
    )
  } catch {
    // Private browsing, or storage is full. The game still works for this tab.
  }
}

/** The last table this device sat at, and whether it still holds that seat. */
export interface Recent {
  code: string
  /** False once the seat has been handed back: the way in is to join again. */
  seated: boolean
}

/**
 * Seats are stored per code and never expire, so this pointer is what
 * separates the table you were last at from every table this browser has ever
 * sat at. It outlives the seat deliberately: somebody who walked out a minute
 * ago is the person most likely to want back in, and the alternative is asking
 * them to remember four characters they never typed.
 *
 * It expires with the lobby itself. Past that the table on the other end is
 * gone, and an offer to return to it would be an offer to nowhere.
 */
export function recallLobby(now = Date.now()): Recent | null {
  try {
    const raw = localStorage.getItem(RECENT_KEY)
    if (!raw) return null
    const recent = JSON.parse(raw) as { code?: unknown; at?: unknown }
    const code = typeof recent.code === 'string' ? recent.code : null
    const at = typeof recent.at === 'number' ? recent.at : 0
    if (!code || now - at > LOBBY_TTL_MS) {
      localStorage.removeItem(RECENT_KEY)
      return null
    }
    return { code, seated: loadSeat(code) !== null }
  } catch {
    return null
  }
}

/**
 * Hands the seat back on this device. The pointer above is left standing: the
 * token is what is gone, not the memory of where it was spent.
 */
export function clearSeat(code: string): void {
  try {
    localStorage.removeItem(key(code))
  } catch {
    /* ignore */
  }
}

export function rememberName(name: string): void {
  try {
    localStorage.setItem(NAME_KEY, name)
  } catch {
    /* ignore */
  }
}

export function recallName(): string {
  try {
    return localStorage.getItem(NAME_KEY) ?? ''
  } catch {
    return ''
  }
}
